/* istanbul ignore file */

// Needs to be at the top here per
// https://github.com/lukeautry/tsoa/issues/902#issuecomment-770932395. The error seems to be
// caused by unrelated imports, but this fixes it.
import "reflect-metadata";

import { setupSentry } from "@aptedge/lib-ui/src/context/SentryContext";
import { store } from "@aptedge/lib-ui/src/redux/store/store";
import { SentryDSN } from "@aptedge/lib-ui/src/utils/config";
import { printRelease } from "@aptedge/lib-ui/src/utils/env";
import {
  dataLayerPush,
  sendEventViaBackend
} from "@aptedge/lib-ui/src/utils/event";
import {
  createQueryClient,
  setupQueryClientPersistence
} from "@aptedge/lib-ui/src/utils/query";
import log from "loglevel";
import * as React from "react";
import "react-app-polyfill/stable";
import { createRoot } from "react-dom/client";
import { ErrorBoundary } from "react-error-boundary";
import { Provider } from "react-redux";
import { RootErrorFallback } from "./components/ErrorHandler/RootErrorFallback";
import { Head } from "./components/Head/Head";
import { getTagManagerConfig } from "./config";
import { AppProviders } from "./context/AppProviders";
import "./index.scss";
import "./tailwind.css";
import { AppRoutes } from "./routes/AppRoutes";

// Only log warnings and errors in Production and Development.
// This can be overridden in the browser console with `log.setLevel()`.
// See: https://github.com/pimterry/loglevel
log.setDefaultLevel(log.levels.WARN);
window.log = log;

setupSentry(SentryDSN.WebApp);

const queryClient = createQueryClient();

/*
Create a root component that receives the store via props
and wraps the App component with Provider, giving props to containers
*/
function Root(): React.ReactElement {
  React.useEffect(() => {
    (async () => {
      try {
        const { default: TagManager } = await import("react-gtm-module");
        TagManager.initialize(getTagManagerConfig());
      } catch (error) {
        window.log?.error?.("Failed to initialize GTM", error);
      }
    })();
  }, []);

  React.useEffect(() => {
    const interval = setInterval(() => {
      let event;
      while ((event = (window.aptedgeEvents ?? []).shift())) {
        sendEventViaBackend(event);
      }
    }, 200);

    // mock PostHog JS package for the benefit of tags in
    // https://tagmanager.google.com/#/container/accounts/6006307610/containers/61461813/workspaces/56/tags
    window.posthog = {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      capture: (event: string, data: any) => {
        dataLayerPush({ event, data });
      }
    };

    return () => clearInterval(interval);
  }, []);

  printRelease();

  return (
    <Provider store={store}>
      <AppProviders queryClient={queryClient}>
        <Head />
        <AppRoutes />
      </AppProviders>
    </Provider>
  );
}

setupQueryClientPersistence(queryClient).then(() => {
  // The app must be rendered after this Promise completes, or queries may not use the cache on
  // initial load. This is very quick, though.
  const container = document.getElementById("root");
  if (container) {
    const root = createRoot(container);
    root.render(
      <ErrorBoundary
        fallbackRender={({ error, resetErrorBoundary }) => (
          <RootErrorFallback
            error={error}
            resetErrorBoundary={resetErrorBoundary}
          />
        )}
      >
        <Root />
      </ErrorBoundary>
    );
  }
});
